import { useState } from 'react';
import { Button, Spinner, Textarea } from '@monksflow/monks-ui';

import { useAskPersonaResearch, type GwiResearchCategory } from '@/entities/gwi-research';

import { formatInsight } from './formatInsight';
import { useTranslation } from 'react-i18next';

interface ResearchAudienceTabProps {
  personaId: string;
  category?: GwiResearchCategory;
}

export function ResearchAudienceTab({ personaId, category }: ResearchAudienceTabProps) {
  const { t } = useTranslation('common');

  const [question, setQuestion] = useState('');
  const { mutate, isPending, data, isError, error } = useAskPersonaResearch(personaId, category);

  const handleSubmit = () => {
    const trimmed = question.trim();
    if (!trimmed || isPending) return;
    mutate(trimmed);
  };

  const hasResult = !!data;
  const hasInsights = !!data && data.insights.length > 0;

  return (
    <div className="relative flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-xl leading-6 font-medium text-foreground">
          {t('components.researchAudienceTab.whatWouldYouLikeTo')}
        </h3>
        <Textarea
          placeholder={t('components.researchAudienceTab.enterQuestions')}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          rows={3}
          disabled={isPending}
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!question.trim() || isPending}>
          {t('components.researchAudienceTab.researchAudience')}
        </Button>
      </div>

      {isError && (
        <p className="text-sm text-destructive">
          {error.message || t('components.researchAudienceTab.somethingWentWrongAskingGwi')}
        </p>
      )}

      {hasInsights && (
        <div className="flex flex-col gap-2">
          {data.insights.map((insight, index) => (
            <div
              key={index}
              className="rounded-lg border border-border bg-muted p-3 text-sm text-foreground"
            >
              {formatInsight(insight)}
            </div>
          ))}
        </div>
      )}

      {hasResult && !hasInsights && (
        <p className="text-sm text-muted-foreground">
          {t('components.researchAudienceTab.noInsightsFoundForThis')}
        </p>
      )}

      {isPending && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
          <Spinner size="xl" />
        </div>
      )}
    </div>
  );
}

ResearchAudienceTab.displayName = 'ResearchAudienceTab';
